import styled from 'styled-components';
import media, { generateMedia } from 'styled-media-query';
import { Button } from '../../../../../atoms/Button';
import { PracticeTitle } from '../PracticeTitle';
import { ProgramElementBackArchive } from '../../ProgramElementBackArchive';
import { ProgramElementPagination } from '../../ProgramElementPagination';
import { ProgramElementsNav } from '../../ProgramElementsNav';
import { CLASS_NAME_MARKDOWN_BODY, Markdown } from '../../../../../../common/Markdown';
import { getRecentPracticeSubmitStatus, getRecentPracticeSubmitStatusWithoutDraft } from '../../practiceSubmitUtil';
import { getChaptersAndPractices } from '../../programUtil';
import WindowHooks from '../../../../../../utils/WindowHooks';
import { PracticeSubmitDetailReviewStatus, PracticeDetailFragment } from '../../../../../../gen/graphql';

interface PracticeDetailProps {
  practiceID: string;
  practice: PracticeDetailFragment | null | undefined;
  hasPermissionToPracticeForInstructorAndCoachRead: boolean;
  onViewPracticeExplanationButtonClick: ((practiceID: string) => void) | undefined;
  hasPermissionToCreatePracticeSubmit: () => boolean;
  onPracticeSubmitCreateButtonClick: (practiceID: string) => void;
  hasPermissionToUpdatePracticeSubmit: () => boolean;
  onPracticeSubmitUpdateButtonClick: ((practiceSubmitID: string, practiceSubmitDetailID: string) => void) | undefined;
  onViewFeedbackButtonClick: ((practiceSubmitID: string) => void) | undefined;
  isLoggedIn: boolean;
}

const CLASS_NAME_PRACTICE = 'practice';

export function PracticeDetailContent(props: PracticeDetailProps): React.ReactElement {
  const myRecentPracticeSubmitStatusWithoutDraft = getRecentPracticeSubmitStatusWithoutDraft(
    props.practice?.mySubmit?.practiceSubmitDetails,
  );
  const myRecentPracticeSubmitStatus = getRecentPracticeSubmitStatus(props.practice?.mySubmit?.practiceSubmitDetails);
  const hasMyPracticeSubmit = Boolean(myRecentPracticeSubmitStatusWithoutDraft);

  const hasMyPracticeSubmitDraft = myRecentPracticeSubmitStatus === PracticeSubmitDetailReviewStatus.Draft;

  const program = props.practice?.program;
  const programElements = getChaptersAndPractices(program);
  const currentPracticeIndex = programElements.findIndex(
    ({ __typename: typename, id }) => typename === 'Practice' && id === props.practiceID,
  );

  const isExplanation =
    myRecentPracticeSubmitStatusWithoutDraft === PracticeSubmitDetailReviewStatus.Passed ||
    props.hasPermissionToPracticeForInstructorAndCoachRead ||
    !props.practice?.isSubmittable;

  const goToPracticeSubmitNewPage = () => {
    if (!props.hasPermissionToCreatePracticeSubmit()) {
      props.onPracticeSubmitCreateButtonClick?.(props.practiceID);
    }
  };

  const goToPracticeSubmitEditPage = () => {
    if (!props.hasPermissionToUpdatePracticeSubmit()) {
      const mySubmitID = props.practice?.mySubmit?.id;
      const practiceSubmitDetails = props.practice?.mySubmit?.practiceSubmitDetails;
      if (mySubmitID && practiceSubmitDetails && practiceSubmitDetails[0].id) {
        props.onPracticeSubmitUpdateButtonClick?.(mySubmitID, practiceSubmitDetails[0].id);
      }
    }
  };

  const goToPracticeSubmitPage = () => {
    if (props.practice?.mySubmit?.id) {
      props.onViewFeedbackButtonClick?.(props.practice.mySubmit.id);
    }
  };

  // この画面に遷移した際に、URLのハッシュがあればその位置までスクロールし、なければページトップまでスクロールする。
  WindowHooks.useScrollToHashOrTop();

  return (
    <>
      <PracticeWrapper>
        <div className={CLASS_NAME_PRACTICE}>
          <Content>
            <StyledPracticeTitle
              practice={props.practice}
              submitReviewStatus={myRecentPracticeSubmitStatusWithoutDraft}
            />
            <MarkdownWrapper>
              <Markdown content={props.practice?.content ?? ''} type="program" />
            </MarkdownWrapper>
          </Content>
          <PageTransitionButtons>
            <div>
              <PageTransitionButton
                border
                disabled={!isExplanation}
                onClick={() => props.onViewPracticeExplanationButtonClick?.(props.practiceID)}
              >
                課題の解説を見る
              </PageTransitionButton>
              {!isExplanation && <ButtonNote>※解説は課題に合格すると見ることができます</ButtonNote>}
            </div>
            {!props.hasPermissionToPracticeForInstructorAndCoachRead &&
              (!hasMyPracticeSubmit || hasMyPracticeSubmitDraft ? (
                props.practice?.isSubmittable ? (
                  <PageTransitionButton
                    onClick={hasMyPracticeSubmitDraft ? goToPracticeSubmitEditPage : goToPracticeSubmitNewPage}
                  >
                    課題を提出する
                  </PageTransitionButton>
                ) : (
                  <ClosedNotice>
                    この課題は取り扱いが終了しているため提出できません。
                    <br />
                    「課題の解説」を参照のうえセルフレビューにて実施をお願いします。
                  </ClosedNotice>
                )
              ) : (
                <PageTransitionButton border onClick={goToPracticeSubmitPage}>
                  課題のレビューを見る
                </PageTransitionButton>
              ))}
          </PageTransitionButtons>
          {program && (
            <ProgramElementPagination
              program={program}
              currentChapterIndex={currentPracticeIndex}
              disabledNext={false}
            />
          )}
          <ProgramElementBackArchive programID={program?.id} practiceID={props.practiceID} isLogin={props.isLoggedIn} />
        </div>
        <ProgramElementsNav programElements={programElements} currentID={props.practiceID} programID={program?.id} />
      </PracticeWrapper>
    </>
  );
}

const customMedia = generateMedia({
  tablet: '1024px',
});

const PracticeWrapper = styled.div`
  transition: all 0.2s;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin: 0 auto;

  ${media.lessThan('medium')`
    padding-bottom: 3rem;
  `}

  .${CLASS_NAME_PRACTICE} {
    flex: 1;
    min-width: 0;

    .${CLASS_NAME_MARKDOWN_BODY} {
      scroll-snap-type: y mandatory;

      h2 {
        scroll-snap-align: start;
        scroll-margin-top: 1em;

        &:first-child {
          scroll-snap-align: start;
          scroll-margin-bottom: -1em;
        }
      }
    }
  }

  ${media.lessThan('large')`
    .${CLASS_NAME_PRACTICE} {
      width: calc(100% - 15rem);
    }
  `}

  ${customMedia.lessThan('tablet')`
    .${CLASS_NAME_PRACTICE} {
      width: 100%;
    }
  `}
`;
const Content = styled.div`
  position: relative;

  svg {
    z-index: 1;
    position: absolute;
    left: 0;
    top: 0;
  }
`;
const StyledPracticeTitle = styled(PracticeTitle)`
  ${media.lessThan('medium')`
    margin: -2rem -1rem 0;
  `}
`;
const MarkdownWrapper = styled.div`
  margin: 0 auto;
  padding: 2rem;
  overflow: hidden;

  ${media.lessThan('medium')`
    padding: 1.5rem 0;
  `}
`;
const PageTransitionButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: start;
  margin: 1.25rem 0 2rem;

  > :first-child {
    margin: 0 3rem 0 0;

    ${media.lessThan('medium')`
      margin-right: 0;
      margin-bottom: 1rem;
    `}
  }

  ${media.lessThan('medium')`
    display: block;
    margin-top: 0.75rem;
  `}
`;

const PAGE_TRANSITION_BUTTON_FONT_SIZE = '12px';

const PageTransitionButton = styled(Button)`
  width: 280px;
  font-size: ${PAGE_TRANSITION_BUTTON_FONT_SIZE};

  ${media.lessThan('medium')`
    width: 100%;
  `}
`;
const ButtonNote = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  text-align: center;
`;

const ClosedNotice = styled.div`
  display: flex;
  height: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;

  font-size: ${PAGE_TRANSITION_BUTTON_FONT_SIZE};
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  ${media.lessThan('medium')`
    height: 44px;
    align-self: stretch;
  `}
`;
